import { ActionType } from '@/actions/actiontypes';
import { Config } from '@/bootstrap/config';
import { HistoricalRoadReference } from '@/domain/HistoricalRoadReference';
import { LegacyVegreferanse } from '@/domain/LegacyVegreferanse';
import { Vegsystemreferanse } from '@/domain/omrader';
import { Veglenkesekvensposisjon } from '@/domain/Veglenkesekvensposisjon';
import { Node } from '@/domain/vegnett/Node';
import { Vegnettlenke } from '@/domain/vegnett/Vegnettlenke';
import { Vegobjekt } from '@/domain/vegobjekter/Vegobjekt';
import { WKTObject } from '@/domain/WKTObject';
import {
    CommonQueries,
    inkluderBar,
    MapQueries,
    OmraderQueries,
    VegnettQueries,
    VegobjekterQueries,
} from '@/middleware/queries';
import { ApiVersion, Severity, VegkartError, VegsystemreferanseLookup } from '@/state';
import { storageHasToken, SVVTOKENS_KEY } from '@/utils/AuthenticationUtils';
import { groupWkts } from '@/utils/SpatialHelper';
import {
    getApiURLVegobjekter,
    getApiURLVegnett,
    getApiVersion,
    isToday,
    tryTransform,
} from '@/utils/Utils';
import axios, { AxiosError, AxiosInstance } from 'axios';
import { URLSearchParams } from 'url';
import * as uuidgen from 'uuid';
import { LogEntry, sendLog } from './logging';
import {
    EksportChangelog,
    ServerStatusResponse,
    StedsnavnResponse,
    VegnettResponse,
    VegobjekterStatisticsResponse,
    VegobjektEgenskaperResponse,
    VegobjektResponse,
} from './responses';
import {
    AllVegobjekterStatisticsResponseGruppert,
    VegobjekterStatisticsByKategoriResponseGruppert,
    VegobjekterStatisticsResponseGruppertByEgenskap,
    VegobjekterStatisticsResponseGruppertByFylke,
    VegobjekterStatisticsResponseGruppertByKommune,
} from './responses/VegobjekterStatisticsResponseGruppert';
import {
    transformAllVegobjekterStatisticsGruppert,
    transformHistoricalRoadReferences,
    transformHistoricalRoadReferencesFromMeter,
    transformNode,
    transformRoadReferenceLookup,
    transformSegmentertVegnettResponse,
    transformServerStatus,
    transformStedsnavnResponse,
    transformVegnettlenkesekvens,
    transformVegnettStatisticsGruppert,
    transformVegobjekt,
    transformVegobjektArray,
    transformVegobjekterStatistics,
    transformVegobjekterStatisticsByKategoriGruppert,
    transformVegobjekterStatisticsGruppertByEgenskap,
    transformVegobjekterStatisticsGruppertByFylke,
    transformVegobjekterStatisticsGruppertByKommune,
    transformVegobjektEgenskaperResponse,
    transformVegobjektResponse,
    transformVegsystemreferanseGeometri,
} from './transformers';

export function getHeaders(clientSessionId: string) {
    return {
        Accept: 'application/vnd.vegvesen.nvdb-v3-rev1+json, application/json',
        'X-Client': 'Vegkart',
        'X-Client-Session': clientSessionId,
    };
}

export const getBlob = async (
    url: string,
    isSensitiveAvailable: boolean
): Promise<Blob> => {
    const headers: HeadersInit = {
        Accept: 'application/vnd.vegvesen.nvdb-v3-rev1+json, application/json',
        'X-Client': 'Vegkart',
        'X-Client-Session': getClientSessionId(),
    };

    if (isSensitiveAvailable) {
        headers['Authorization'] = getClientAuthToken();
    }

    const response = await fetch(url, {
        headers,
        mode: 'cors',
    });

    return await response.blob();
};

export function getHeadersWithAuth(clientSessionId: string) {
    return {
        Authorization: getClientAuthToken(),
        Accept: 'application/vnd.vegvesen.nvdb-v3-rev1+json, application/json',
        'X-Client': 'Vegkart',
        'X-Client-Session': clientSessionId,
    };
}

function getClientAuthToken(): string {
    const tokensAsString = sessionStorage.getItem(SVVTOKENS_KEY);
    const tokens: { access_token: string } = JSON.parse(tokensAsString ?? 'null');
    return `Bearer ${tokens.access_token}`;
}

export function getClientSessionId(): string {
    let clientSessionId = sessionStorage.getItem('clientSessionId');
    if (!clientSessionId) {
        clientSessionId = uuidgen.v4();
        sessionStorage.setItem('clientSessionId', clientSessionId);
    }
    return clientSessionId;
}

const LEGACY_CATEGORY_ENUM_ID = new Map<string, number>([
    ['E', 5492],
    ['R', 5493],
    ['F', 5494],
    ['K', 5495],
    ['P', 5496],
    ['S', 5497],
]);

const LEGACY_STATUS_ENUM_ID = new Map<string, number>([
    ['V', 5499],
    ['W', 5505],
    ['T', 5502],
    ['S', 5504],
    ['G', 12159],
    ['U', 12983],
    ['B', 13707],
    ['M', 5501],
    ['X', 5500],
    ['A', 7041],
    ['H', 12160],
    ['P', 7042],
    ['E', 7046],
    ['Q', 12986],
]);

function createLegacyVegreferanseFilter(vegreferanse: LegacyVegreferanse): string {
    return [
        `(4566=${LEGACY_CATEGORY_ENUM_ID.get(vegreferanse.kategori)})`,
        `(4567=${LEGACY_STATUS_ENUM_ID.get(vegreferanse.status)})`,
        `(4568=${vegreferanse.vegnummer})`,
        `(4569=${vegreferanse.hp})`,
        `(4571<${vegreferanse.meter + 1})`,
        `(4572>${vegreferanse.meter - 1})`,
        `(4591=${vegreferanse.fylke})`,
        `(4592=${vegreferanse.kommune})`,
    ].join('AND');
}

function doFetch(url: string, signal?: AbortSignal) {
    return fetch(url.toString(), {
        headers: storageHasToken()
            ? getHeadersWithAuth(getClientSessionId())
            : getHeaders(getClientSessionId()),
        mode: 'cors',
        signal,
    });
}

export function isRequestAborted(error: unknown): boolean {
    if (axios.isCancel(error)) return true;
    if (error instanceof AxiosError) return error.code === 'ERR_CANCELED';
    if (error instanceof DOMException) return error.name === 'AbortError';
    const errorRecord =
        typeof error === 'object' && error !== null
            ? (error as Record<string, unknown>)
            : null;

    return (
        errorRecord !== null &&
        (errorRecord.code === 'ERR_CANCELED' ||
            errorRecord.name === 'CanceledError' ||
            errorRecord.name === 'AbortError')
    );
}

export interface ApiErrorMessage {
    code: number;
    message: string;
    message_detailed?: string;
    help_url: string;
}

export class ApiError extends Error {
    private __proto__: ApiError;
    constructor(readonly msg: ApiErrorMessage) {
        super(msg.message);
        this.name = this.constructor.name;
        this.__proto__ = ApiError.prototype;
    }
}

export class AuthenticationError extends Error {
    private __proto__: AuthenticationError;
    constructor(msg: string) {
        super(msg);
        this.name = this.constructor.name;
        this.__proto__ = AuthenticationError.prototype;
    }
}

const LOG_DELAY_CUTOFF = 5000; // Time in ms to check against when deciding if a performance log entry should be made.

export class Server {
    private vegobjekterApiUrl: string;
    private vegnettApiUrl: string;
    private axios: AxiosInstance;
    private SSRIAxios: AxiosInstance;
    private cacheName = 'vegkart-cache';
    private clientSessionId: string = getClientSessionId();
    private readonly eksport_url: string;
    private readonly changelogUrl: string;

    constructor(private config: Config) {
        this.vegobjekterApiUrl = getApiURLVegobjekter(config);
        this.vegnettApiUrl = getApiURLVegnett(config);

        const apiURL =
            getApiVersion() === ApiVersion.V3
                ? config.restapi_url_v3
                : config.restapi_url;
        this.axios = axios.create({
            baseURL: apiURL,
            headers: getHeaders(this.clientSessionId),
            timeout: config.ajaxTimeout,
        });
        this.SSRIAxios = axios.create({
            baseURL: config.url_SSRI,
            timeout: config.ajaxTimeout,
        });
        this.eksport_url = config.eksport_url;
        this.changelogUrl = config.changelog_url;

        // remove existing cache on startup
        if ('caches' in window) {
            // noinspection JSIgnoredPromiseFromCall
            caches.delete(this.cacheName);
        }
    }

    // ----- Meta-methods begin ----- //
    getServerStatus(): Promise<ServerStatusResponse> {
        return new Promise<ServerStatusResponse>((resolve, reject) => {
            this.axios
                .get('status', {
                    transformResponse: tryTransform(transformServerStatus),
                })
                .then(response => resolve(response.data))
                .catch(reject);
        });
    }

    public updateApiUrl() {
        this.vegobjekterApiUrl = getApiURLVegobjekter(this.config);
        this.vegnettApiUrl = getApiURLVegnett(this.config);

        const apiURL =
            getApiVersion() === ApiVersion.V3
                ? this.config.restapi_url_v3
                : this.config.restapi_url;
        this.axios.defaults.baseURL = apiURL;
    }

    postErrorReport(error: VegkartError) {
        const logEntry: LogEntry = {
            data: { params: error.params, stack: error.stack },
            hash: window.location.hash,
            message: error.message,
            session: this.clientSessionId,
            severity: error.severity,
            agent: window.navigator.userAgent,
        };
        return sendLog(logEntry);
    }

    postInfoLogEntry(message: string, data?: Record<string, string | number>) {
        return sendLog({
            message,
            session: this.clientSessionId,
            hash: window.location.hash,
            severity: Severity.INFO,
            agent: window.navigator.userAgent,
            data: { ...data },
        });
    }

    postPerformanceLogEntry(
        action: ActionType,
        milliseconds: number,
        additionalData?: Record<string, string | number>
    ) {
        if (milliseconds > LOG_DELAY_CUTOFF) {
            const entry: LogEntry = {
                message: 'Slow action detected!',
                session: this.clientSessionId,
                hash: window.location.hash,
                severity: Severity.INFO,
                agent: window.navigator.userAgent,
                data: {
                    action: ActionType[action],
                    milliseconds,
                    ...additionalData,
                },
            };

            const isDevelopment = process.env.NODE_ENV === 'development';
            if (isDevelopment) {
                console.group('Performance Log Entry');
                console.info(entry);
                console.groupEnd();
            } else {
                return sendLog(entry);
            }
        }
    }

    // ----- Meta-methods end ----- //

    // ----- Vegobjekt-methods begin ----- //
    getVegobjekt(
        vegobjektId: number,
        geometryTolerance?: number,
        typeId?: number,
        version?: number,
        includeParam = 'lokasjon,metadata,egenskaper,relasjoner,geometri'
    ): Promise<Vegobjekt> {
        const path = typeId
            ? version
                ? `/vegobjekter/${typeId}/${vegobjektId}/${version}`
                : `/vegobjekter/${typeId}/${vegobjektId}`
            : `/vegobjekt`;
        const url = this.vegobjekterApiUrl + path;

        const params = typeId
            ? {
                  dybde: includeParam.includes('relasjoner') ? 1 : undefined, // dybde requires relasjoner
                  inkluder: includeParam,
                  geometritoleranse: geometryTolerance,
              }
            : {
                  id: vegobjektId,
                  geometritoleranse: geometryTolerance,
              };
        return new Promise<Vegobjekt>((resolve, reject) => {
            this.axios
                .get(url, {
                    params,
                    transformResponse: tryTransform(transformVegobjekt),
                })
                .then(response => resolve(response.data))
                .catch(reject);
        });
    }

    // This function is sometimes used as a preliminary step to find the latest version of a vegobjekt
    // because of redirect-issues in the Edge browser.
    getVegobjektVersionsMinimal(
        vegobjektId: number,
        typeId: number,
        includeParam = 'metadata'
    ): Promise<Vegobjekt[]> {
        const url = `${this.vegobjekterApiUrl}/vegobjekter/${typeId}/${vegobjektId}/versjoner`;

        const params = {
            inkluder: includeParam,
        };

        return new Promise<Vegobjekt[]>((resolve, reject) => {
            this.axios
                .get(url, {
                    params,
                    transformResponse: tryTransform(transformVegobjektArray),
                })
                .then(response => resolve(response.data))
                .catch(reject);
        });
    }

    getVegobjekter(
        typeId: number,
        commonQueries: CommonQueries,
        mapQueries: MapQueries,
        omraderQueries: OmraderQueries,
        vegnettQueries: VegnettQueries,
        vegobjekterQueries: VegobjekterQueries,
        timestamp: Date | null,
        signal?: AbortSignal
    ): Promise<VegobjektResponse> {
        const extraInkluder: inkluderBar[] =
            typeId === 580 || typeId === 704 ? ['egenskaper'] : [];
        const inkluder: inkluderBar[] = Array.from(
            new Set([
                ...vegobjekterQueries.inkluder,
                'metadata',
                'lokasjon',
                'geometri',
                ...extraInkluder,
            ])
        );
        let allParams = {
            ...commonQueries.toParams(),
            ...mapQueries.toParams(),
            ...omraderQueries.toParams(),
            ...vegnettQueries.toParams(),
        };
        if (timestamp == null || isToday(timestamp))
            allParams = {
                ...allParams,
                ...vegobjekterQueries.withInkluder(inkluder).toParams(),
            };
        else
            allParams = {
                ...allParams,
                ...vegobjekterQueries
                    .withInkluder(inkluder)
                    .withTidspunkt(timestamp)
                    .toParams(),
            };
        return new Promise<VegobjektResponse>((resolve, reject) => {
            const url = `${this.vegobjekterApiUrl}/vegobjekter/${typeId}`;

            this.axios
                .get(url, {
                    params: allParams,
                    signal,
                    transformResponse: tryTransform(transformVegobjektResponse),
                })
                .then(response => resolve(response.data))
                .catch(reject);
        });
    }

    getVegobjekterEgenskaper(
        typeId: number,
        commonQueries: CommonQueries,
        mapQueries: MapQueries,
        omraderQueries: OmraderQueries,
        vegnettQueries: VegnettQueries,
        vegobjekterQueries: VegobjekterQueries,
        timestamp: Date | null,
        signal?: AbortSignal
    ): Promise<VegobjektEgenskaperResponse> {
        const queries = vegobjekterQueries
            .withInkluder(['egenskaper'])
            .withInkluderEgenskaper(['basis']);
        const allParams = {
            ...commonQueries.toParams(),
            ...mapQueries.toParams(),
            ...omraderQueries.toParams(),
            ...vegnettQueries.toParams(),
            ...(timestamp == null || isToday(timestamp)
                ? queries.toParams()
                : queries.withTidspunkt(timestamp).toParams()),
        };
        return new Promise<VegobjektEgenskaperResponse>((resolve, reject) => {
            const url = `${this.vegobjekterApiUrl}/vegobjekter/${typeId}`;

            this.axios
                .get(url, {
                    params: allParams,
                    signal,
                    transformResponse: tryTransform(transformVegobjektEgenskaperResponse),
                })
                .then(response => resolve(response.data))
                .catch(reject);
        });
    }

    getVegobjekterStatistics(
        typeId: number,
        mapQueries: MapQueries,
        omraderQueries: OmraderQueries,
        vegnettQueries: VegnettQueries,
        vegobjekterQueries: VegobjekterQueries,
        timestamp: Date | null,
        signal?: AbortSignal
    ): Promise<VegobjekterStatisticsResponse> {
        const url = new URL(`${this.vegobjekterApiUrl}/vegobjekter/${typeId}/statistikk`);

        let allParams = {
            ...mapQueries.toParams(),
            ...omraderQueries.toParams(),
            ...vegnettQueries.toParams(),
        };
        if (timestamp == null || isToday(timestamp))
            allParams = { ...allParams, ...vegobjekterQueries.toParams() };
        else
            allParams = {
                ...allParams,
                ...vegobjekterQueries.withTidspunkt(timestamp).toParams(),
            };

        Object.keys(allParams).forEach(key =>
            url.searchParams.append(key, allParams[key])
        );

        return this.tryCache(url, signal)
            .then(async response => {
                if (response.status === 422) throw new ApiError(await response.json());
                else if (response.status === 404) {
                    const body = await response
                        .json()
                        .then(b => (b instanceof Array ? b[0] : b));
                    throw new ApiError(body);
                } else if (response.status === 403)
                    throw new AuthenticationError(await response.text());
                else if (response.status === 401)
                    throw new AuthenticationError(await response.text());
                return response.text();
            })
            .then(tryTransform(transformVegobjekterStatistics));
    }

    getVegobjekterStatisticsGruppertByEgenskap(
        typeId: number,
        egenskapstypeId: number,
        mapQueries: MapQueries,
        omraderQueries: OmraderQueries,
        vegnettQueries: VegnettQueries,
        vegobjekterQueries: VegobjekterQueries,
        timestamp: Date | null = null,
        signal?: AbortSignal
    ): Promise<VegobjekterStatisticsResponseGruppertByEgenskap[]> {
        const url = new URL(
            `${this.vegobjekterApiUrl}/vegobjekter/${typeId}/statistikk/gruppert/egenskap`
        );
        url.searchParams.append('egenskapstypeId', egenskapstypeId.toString());

        let allParams = {
            ...mapQueries.toParams(),
            ...omraderQueries.toParams(),
            ...vegnettQueries.toParams(),
        };
        if (timestamp == null || isToday(timestamp))
            allParams = { ...allParams, ...vegobjekterQueries.toParams() };
        else
            allParams = {
                ...allParams,
                ...vegobjekterQueries.withTidspunkt(timestamp).toParams(),
            };

        Object.keys(allParams).forEach(key =>
            url.searchParams.append(key, allParams[key])
        );

        return this.tryCache(url, signal)
            .then(async response => {
                if (response.status === 422) throw new ApiError(await response.json());
                else if (response.status === 404) {
                    const body = await response
                        .json()
                        .then(b => (b instanceof Array ? b[0] : b));
                    if (body.code === 4015) throw new ApiError(body);
                    throw new ApiError(body);
                } else if (response.status === 403)
                    throw new AuthenticationError(await response.text());
                else if (response.status === 401)
                    throw new AuthenticationError(await response.text());
                return response.text();
            })
            .then(tryTransform(transformVegobjekterStatisticsGruppertByEgenskap));
    }

    getVegobjekterStatisticsGruppertByFylke(
        typeId: number,
        mapQueries: MapQueries,
        omraderQueries: OmraderQueries,
        vegnettQueries: VegnettQueries,
        vegobjekterQueries: VegobjekterQueries,
        timestamp: Date | null,
        signal?: AbortSignal
    ): Promise<VegobjekterStatisticsResponseGruppertByFylke[]> {
        const allParams = {
            ...mapQueries.toParams(),
            ...omraderQueries.toParams(),
            ...vegnettQueries.withTidspunkt(timestamp).toParams(),
            ...vegobjekterQueries.toParams(),
        };
        const url = new URL(
            `${this.vegobjekterApiUrl}/vegobjekter/${typeId}/statistikk/gruppert?gruppering=fylke&inkluder=alle`
        );
        Object.keys(allParams).forEach(key =>
            url.searchParams.append(key, allParams[key])
        );
        return this.tryCache(url, signal)
            .then(async response => {
                if (response.status === 422) throw new ApiError(await response.json());
                else if (response.status === 404) {
                    const body = await response
                        .json()
                        .then(b => (b instanceof Array ? b[0] : b));
                    if (body.code === 4015) throw new ApiError(body);
                } else if (response.status === 403)
                    throw new AuthenticationError(await response.text());
                return response.text();
            })
            .then(tryTransform(transformVegobjekterStatisticsGruppertByFylke));
    }

    getVegobjekterStatisticsGruppertByKommune(
        typeId: number,
        mapQueries: MapQueries,
        omraderQueries: OmraderQueries,
        vegnettQueries: VegnettQueries,
        vegobjekterQueries: VegobjekterQueries,
        timestamp: Date | null,
        signal?: AbortSignal
    ): Promise<VegobjekterStatisticsResponseGruppertByKommune[]> {
        const allParams = {
            ...mapQueries.toParams(),
            ...omraderQueries.toParams(),
            ...vegnettQueries.withTidspunkt(timestamp).toParams(),
            ...vegobjekterQueries.toParams(),
        };
        const url = new URL(
            `${this.vegobjekterApiUrl}/vegobjekter/${typeId}/statistikk/gruppert?gruppering=kommune&inkluder=alle`
        );
        Object.keys(allParams).forEach(key =>
            url.searchParams.append(key, allParams[key])
        );
        return this.tryCache(url, signal)
            .then(async response => {
                if (response.status === 422) throw new ApiError(await response.json());
                else if (response.status === 404) {
                    const body = await response
                        .json()
                        .then(b => (b instanceof Array ? b[0] : b));
                    if (body.code === 4015) throw new ApiError(body);
                } else if (response.status === 403)
                    throw new AuthenticationError(await response.text());
                return response.text();
            })
            .then(tryTransform(transformVegobjekterStatisticsGruppertByKommune));
    }

    getVegnettStatisticsGruppert(
        kartutsnitt: string,
        gruppering: string[] = [],
        signal?: AbortSignal
    ) {
        const url = new URL(
            this.vegnettApiUrl + '/veglenkesekvenser/segmentert/statistikk/gruppert'
        );
        if (gruppering.length != 0)
            url.searchParams.append('gruppering', gruppering.join(','));
        url.searchParams.append('kartutsnitt', kartutsnitt);

        return this.tryCache(url, signal)
            .then(async response => {
                if (response.status === 422) throw new ApiError(await response.json());
                else if (response.status === 404) {
                    const body = await response
                        .json()
                        .then(b => (b instanceof Array ? b[0] : b));
                    if (body.code === 4015) throw new ApiError(body);
                } else if (response.status === 403)
                    throw new AuthenticationError(await response.text());
                return response.text();
            })
            .then(tryTransform(transformVegnettStatisticsGruppert));
    }

    getVegobjekterStatisticsByKategoriGruppert(
        typeId: number,
        kartutsnitt?: string,
        signal?: AbortSignal
    ): Promise<VegobjekterStatisticsByKategoriResponseGruppert[]> {
        const url = new URL(
            `${this.vegobjekterApiUrl}/vegobjekter/${typeId}/statistikk/gruppert?${
                kartutsnitt ? 'kartutsnitt=' + kartutsnitt + '&' : ''
            }gruppering=vegkategori&inkluder=alle`
        );

        return this.tryCache(url, signal)
            .then(async response => {
                if (response.status === 422) throw new ApiError(await response.json());
                else if (response.status === 404) {
                    const body = await response
                        .json()
                        .then(b => (b instanceof Array ? b[0] : b));
                    if (body.code === 4015) throw new ApiError(body);
                } else if (response.status === 403)
                    throw new AuthenticationError(await response.text());
                return response.text();
            })
            .then(tryTransform(transformVegobjekterStatisticsByKategoriGruppert));
    }

    getAllVegobjekterStatisticsGruppert(
        typeId: number,
        kartutsnitt?: string,
        signal?: AbortSignal
    ): Promise<AllVegobjekterStatisticsResponseGruppert[]> {
        const url = new URL(
            `${this.vegobjekterApiUrl}/vegobjekter/${typeId}/statistikk/gruppert?${
                kartutsnitt ? 'kartutsnitt=' + kartutsnitt + '&' : ''
            }gruppering=fylke&gruppering=kommune&gruppering=vegkategori&inkluder=alle`
        );

        return this.tryCache(url, signal)
            .then(async response => {
                if (response.status === 422) throw new ApiError(await response.json());
                else if (response.status === 404) {
                    const body = await response
                        .json()
                        .then(b => (b instanceof Array ? b[0] : b));
                    if (body.code === 4015) throw new ApiError(body);
                } else if (response.status === 403)
                    throw new AuthenticationError(await response.text());
                return response.text();
            })
            .then(tryTransform(transformAllVegobjekterStatisticsGruppert));
    }

    /**
     * Check if URL is cached, otherwise perform request and cache the result.
     * Falls back to plain fetch if cache is unsupported
     */
    async tryCache(url: URL, signal?: AbortSignal): Promise<Response> {
        if ('caches' in window) {
            const cache = await caches.open(this.cacheName);
            let response = await cache.match(url.toString());
            if (!response) {
                response = await doFetch(url.toString(), signal);
                await cache.put(url.toString(), response.clone());
            }
            return response;
        } else return doFetch(url.toString(), signal);
    }

    async tryCacheWithFallback(primaryUrl: URL, fallbackUrl: URL): Promise<Response> {
        const fetchAndCache = async (url: URL) => {
            if ('caches' in window) {
                const cache = await caches.open(this.cacheName);
                let response = await cache.match(url.toString());
                if (!response) {
                    response = await doFetch(url.toString());
                    if (!response.ok)
                        throw new Error(
                            `Failed to fetch: ${response.status} ${response.statusText}`
                        );
                    await cache.put(url.toString(), response.clone());
                }
                return response;
            } else {
                const response = await doFetch(url.toString());
                if (!response.ok)
                    throw new Error(
                        `Failed to fetch: ${response.status} ${response.statusText}`
                    );
                return response;
            }
        };

        try {
            return await fetchAndCache(primaryUrl);
        } catch (error) {
            console.error('Failed to fetch from the primary URL:', error);

            try {
                return await fetchAndCache(fallbackUrl);
            } catch (fallbackError) {
                console.error('Failed to fetch from the fallback URL:', fallbackError);
                throw fallbackError;
            }
        }
    }

    // ----- Vegobjekt-methods end ----- //

    // ----- Vegnett-methods begin ----- //

    getVeglenkesekvens(
        veglenkesekvensId: number,
        historisk?: boolean | null,
        tidspunkt?: string | null
    ): Promise<Vegnettlenke[]> {
        return new Promise<Vegnettlenke[]>((resolve, reject) => {
            const params = new URLSearchParams();
            if (historisk) params.append('historisk', String(historisk));
            if (tidspunkt) params.append('tidspunkt', tidspunkt);

            let url = `${this.vegnettApiUrl}/veglenkesekvenser/segmentert/${veglenkesekvensId}`;

            if (params.size != 0) {
                url += '?' + params.toString();
            }

            this.axios
                .get(url)
                .then(response =>
                    tryTransform(transformVegnettlenkesekvens)(
                        JSON.stringify(response.data)
                    )
                )
                .then(resolve)
                .catch((e: AxiosError) =>
                    e.response?.status === 404 ? reject(404) : reject(e)
                );
        });
    }

    getVeg(vegsystemreferanse: Vegsystemreferanse): Promise<WKTObject> {
        const params = {
            vegsystemreferanse: vegsystemreferanse.toParams(),
        };
        return this.axios
            .get(`/veg`, { params })
            .then(response =>
                tryTransform(transformVegsystemreferanseGeometri)(
                    JSON.stringify(response.data)
                )
            )
            .catch(e => {
                console.warn('throwing an error from getVeg!');
                if (e.response?.status === 404) throw 404;
                else throw e;
            });
    }

    private getVegobjekttypeIdFromVegsystemreferanse(
        vegsystemreferanse: Vegsystemreferanse
    ): number {
        if (vegsystemreferanse.sideanlegg) return 920;
        if (vegsystemreferanse.kryssystem) return 918;
        return 916;
    }

    getGeometryOnStrekning(vegsystemreferanse: Vegsystemreferanse): Promise<WKTObject> {
        const params = {
            vegsystemreferanse: vegsystemreferanse.toParams(),
            kommune: vegsystemreferanse.kommune ?? undefined,
        };

        const typeId = this.getVegobjekttypeIdFromVegsystemreferanse(vegsystemreferanse);
        const url = `${this.vegobjekterApiUrl}/vegobjekter/${typeId}?inkluder=geometri`;

        return this.axios
            .get(url, { params })
            .then(response =>
                response.data.objekter.map((o: { geometri: WKTObject }) => o.geometri)
            )
            .then(groupWkts)
            .catch(e => {
                console.warn('throwing an error from getGeometryOnStrekning!');
                if (e.response?.status === 404) throw 404;
                else throw e;
            });
    }

    getGeometryOnVegnettsegment(
        vegsystemreferanse: Vegsystemreferanse
    ): Promise<WKTObject> {
        const params = {
            vegsystemreferanse: vegsystemreferanse.toParams(),
            kommune: vegsystemreferanse.kommune ?? undefined,
        };

        const url = `${this.vegnettApiUrl}/veglenkesekvenser/segmentert?`;

        return this.axios
            .get(url, { params })
            .then(response =>
                response.data.objekter.map((o: { geometri: WKTObject }) => o.geometri)
            )
            .then(groupWkts)
            .catch(e => {
                console.warn('throwing an error from getGeometryOnVegnettsegment!');
                if (e.response?.status === 404) throw 404;
                else throw e;
            });
    }

    getNode(id: number): Promise<Node> {
        const url = `${this.vegnettApiUrl}/noder/${id}`;

        return this.axios
            .get(url)
            .then(response => tryTransform(transformNode)(JSON.stringify(response.data)))
            .catch(e => {
                if (e.response?.status === 404) throw 404;
                else throw e;
            });
    }

    getSegmentertVegnett(
        commonQueries: CommonQueries,
        mapQueries: MapQueries,
        omraderQueries: OmraderQueries,
        vegnettQueries: VegnettQueries,
        timestamp: Date | null,
        signal?: AbortSignal
    ): Promise<VegnettResponse> {
        const allParams = {
            ...commonQueries.toParams(),
            ...mapQueries.toParams(),
            ...omraderQueries.toParams(),
            ...vegnettQueries.withTidspunkt(timestamp).toParams(),
        };

        const url = `${this.vegnettApiUrl}/veglenkesekvenser/segmentert`;

        return this.axios
            .get(url, { params: allParams, signal })
            .then(response =>
                tryTransform(transformSegmentertVegnettResponse)(
                    JSON.stringify(response.data)
                )
            );
    }

    // ----- Vegnett-methods end ----- //

    // ----- Coordinate- and placename-methods begin ----- //
    getVSRFromCoordinate(
        coordinate: [number, number],
        timestamp?: Date
    ): Promise<VegsystemreferanseLookup> {
        const tidspunkt = timestamp?.toISOString()?.split('T')?.[0];
        return this.axios
            .get('posisjon', {
                params: {
                    maks_avstand: 200,
                    nord: coordinate[1],
                    ost: coordinate[0],
                    tidspunkt,
                },
            })
            .then(response => {
                if (
                    !response.data.length &&
                    Array.isArray(response.data) &&
                    getApiVersion() !== ApiVersion.V3
                ) {
                    return Promise.reject(404);
                }
                return tryTransform(transformRoadReferenceLookup)(
                    JSON.stringify(response.data)
                );
            })
            .then(vsrLookup => this.wrapWithEndDate(vsrLookup, tidspunkt))
            .catch(
                (e: AxiosError) =>
                    new Promise((resolve, reject) => {
                        if (e.response?.status === 404) reject(404);
                        else reject(e);
                    })
            );
    }

    wrapWithEndDate = async (vsrLookup: VegsystemreferanseLookup, tidspunkt?: string) => {
        const veglenke = vsrLookup?.veglenke;
        if (!tidspunkt || !veglenke) {
            return vsrLookup;
        }

        const links = (
            await this.getVeglenkesekvens(veglenke.id, null, tidspunkt)
        ).filter(
            v =>
                v.startposisjon <= veglenke.relativPosisjon &&
                v.sluttposisjon >= veglenke.relativPosisjon
        );

        if (links.length > 1) console.warn('Found more than 1 historical segment');

        return vsrLookup.withEndDate(links?.[0]?.metadata?.sluttdato ?? null);
    };

    getCoordinateFromVegsystemreferanse(
        roadsysref: string
    ): Promise<VegsystemreferanseLookup | VegkartError> {
        return new Promise<VegsystemreferanseLookup | VegkartError>((resolve, reject) => {
            this.axios
                .get('veg', {
                    params: {
                        vegsystemreferanse: roadsysref,
                    },
                })
                .then(response =>
                    tryTransform(transformRoadReferenceLookup)(
                        JSON.stringify(response.data)
                    )
                )
                .then(resolve)
                .catch((e: AxiosError) =>
                    e.response?.status === 404 ? reject(404) : reject(e)
                );
        });
    }

    getVSRFromVeglenkesekvens(
        veglenkesekvens: string,
        signal?: AbortSignal
    ): Promise<VegsystemreferanseLookup | VegkartError> {
        return new Promise<VegsystemreferanseLookup | VegkartError>((resolve, reject) => {
            this.axios
                .get('veg', {
                    params: {
                        veglenkesekvens,
                    },
                    signal,
                })
                .then(response =>
                    tryTransform(transformRoadReferenceLookup)(
                        JSON.stringify(response.data)
                    )
                )
                .then(resolve)
                .catch((e: AxiosError) =>
                    e.response?.status === 404 ? reject(404) : reject(e)
                );
        });
    }

    getHistoricalRoadReferences(
        veglenke: Veglenkesekvensposisjon,
        timestamp?: Date
    ): Promise<HistoricalRoadReference[]> {
        const params: Record<string, string | boolean> = {
            segmentering: true,
            inkluder: 'egenskaper,lokasjon,metadata',
            veglenkesekvens: `${veglenke.relativPosisjon}@${veglenke.id}`,
        };
        if (timestamp) {
            params.tidspunkt = timestamp.toISOString().split('T')[0];
        } else {
            params.alle_versjoner = true;
        }

        return this.axios
            .get(`${this.vegobjekterApiUrl}/vegobjekter/532`, { params })
            .then(response =>
                transformHistoricalRoadReferences(response.data, veglenke.relativPosisjon)
            );
    }

    getHistoricalRoadReferencesFromLegacyVegreferanse(
        vegreferanse: LegacyVegreferanse,
        timestamp?: Date,
        signal?: AbortSignal
    ): Promise<HistoricalRoadReference[]> {
        const params: Record<string, string | boolean> = {
            inkludergeometri: 'ingen',
            inkluder: 'egenskaper,lokasjon,metadata',
            egenskap: createLegacyVegreferanseFilter(vegreferanse),
        };
        if (timestamp) {
            params.tidspunkt = timestamp.toISOString().split('T')[0];
        } else {
            params.alle_versjoner = true;
        }

        return this.axios
            .get(`${this.vegobjekterApiUrl}/vegobjekter/532`, { params, signal })
            .then(response =>
                transformHistoricalRoadReferencesFromMeter(
                    response.data,
                    vegreferanse.meter
                )
            );
    }

    getStedsnavn(query: string, page: number = 1): Promise<StedsnavnResponse> {
        const queryParams = {
            sok: query.replace(/[;?"']/g, '') + '*',
            utkoordsys: 25833,
            filtrer:
                'metadata.totaltAntallTreff,navn.stedsnavn,navn.stedsnummer,navn.kommuner,navn.fylker,navn.representasjonspunkt,navn.navneobjekttype',
            fuzzy: true,
            treffPerSide: this.config.placenames_page_size,
            side: page,
        };
        return new Promise<StedsnavnResponse>((resolve, reject) => {
            this.SSRIAxios.get('/sted', {
                params: queryParams,
                transformResponse: tryTransform(transformStedsnavnResponse),
            })
                .then(response => resolve(response.data))
                .catch(reject);
        });
    }

    // ----- Coordinate- and placename-methods end ----- //

    setSVVToken(accessToken?: string) {
        if (accessToken)
            this.axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        else delete this.axios.defaults.headers.common['Authorization'];
    }

    async getExportChangelog(): Promise<EksportChangelog> {
        const response = await this.tryCache(
            new URL('/changelog/latest', this.eksport_url)
        );
        return (await response.json()) as Promise<EksportChangelog>;
    }

    async getChangelog(): Promise<string> {
        const response = await fetch(this.changelogUrl);
        const json = await response.json();
        return b64DecodeUnicode(json['content']).split('\n').slice(2).join('\n');
    }

    // ----- Bootstrap methods end ----- //
}

function b64DecodeUnicode(str: string): string {
    return decodeURIComponent(
        Array.prototype.map
            .call(atob(str), function (c: string) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            })
            .join('')
    );
}
